import { useEffect, useState, useCallback, useRef } from 'react';
import { fetchWorkspaces, createWorkspace as apiCreateWorkspace, updateWorkspace as apiUpdateWorkspace } from '../api/workspace.api';
import { useAppStore } from '../store/appStore';
import { setActiveWorkspaceId as setClientWorkspaceId } from '../api/client';
import type { Workspace } from '../types';

export const useWorkspaces = () => {
  const {
    workspaces,
    activeWorkspaceId,
    setWorkspaces,
    setActiveWorkspaceId,
  } = useAppStore();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loadingRef = useRef(false);

  const load = useCallback(async () => {
    // Prevent concurrent loads
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchWorkspaces();
      setWorkspaces(data);

      if (data.length > 0) {
        const savedId = window.localStorage.getItem('vina_active_workspace');
        const existing =
          (savedId && data.find((w) => w.id === savedId)) || data[0];
        setActiveWorkspaceId(existing.id);
        setClientWorkspaceId(existing.id);
      } else {
        setActiveWorkspaceId(null);
        setClientWorkspaceId(null);
      }
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        'We could not load your workspaces. Please try again in a moment.';
      setError(message);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [setActiveWorkspaceId, setWorkspaces]);

  useEffect(() => {
    if (workspaces.length === 0 && !loading && !error && !loadingRef.current) {
      void load();
    }
  }, [error, load, loading, workspaces.length]);

  const selectWorkspace = useCallback(
    (id: string) => {
      setActiveWorkspaceId(id);
      setClientWorkspaceId(id);
      window.localStorage.setItem('vina_active_workspace', id);
    },
    [setActiveWorkspaceId],
  );

  const createWorkspace = useCallback(
    async (payload: Pick<Workspace, 'name'>) => {
      const created = await apiCreateWorkspace({ name: payload.name });
      // Backend auto-generates slug and sets status to ACTIVE
      const nextWorkspaces = [...workspaces, created];
      setWorkspaces(nextWorkspaces);
      selectWorkspace(created.id);
      return created;
    },
    [selectWorkspace, setWorkspaces, workspaces],
  );

  const saveWorkspace = useCallback(
    async (id: string, updates: Partial<Workspace>) => {
      const updated = await apiUpdateWorkspace(id, {
        name: updates.name,
        status: updates.status,
      });
      const nextWorkspaces = workspaces.map((w) => (w.id === id ? updated : w));
      setWorkspaces(nextWorkspaces);
      return updated;
    },
    [setWorkspaces, workspaces],
  );

  return {
    workspaces,
    activeWorkspaceId,
    loading,
    error,
    reload: load,
    selectWorkspace,
    createWorkspace,
    saveWorkspace,
  };
};

